const MENU_SELECTOR = '[data-bf-mobile-menu]';

function syncPageLock() {
  document.documentElement.classList.toggle(
    'bf-mobile-menu-open',
    Boolean(document.querySelector(`${MENU_SELECTOR}[open]`))
  );
}

function closeMenu(menu) {
  if (!(menu instanceof HTMLDetailsElement)) return;
  menu.removeAttribute('open');
  syncPageLock();
}

function initMenu(menu) {
  if (!(menu instanceof HTMLDetailsElement) || menu.dataset.bfMenuReady === 'true') return;
  menu.dataset.bfMenuReady = 'true';

  menu.addEventListener('toggle', syncPageLock);
  menu.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-bf-menu-close]')) {
      event.preventDefault();
      closeMenu(menu);
      return;
    }

    if (target.closest('.bf-header__mobile-panel a[href]')) {
      closeMenu(menu);
    }
  });
}

function initMenus(root = document) {
  root.querySelectorAll(MENU_SELECTOR).forEach(initMenu);
  syncPageLock();
}

initMenus();

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  document.querySelectorAll(`${MENU_SELECTOR}[open]`).forEach(closeMenu);
});

document.addEventListener('shopify:section:load', (event) => {
  const root = event.target instanceof Element ? event.target : document;
  initMenus(root);
});

document.addEventListener('shopify:section:unload', syncPageLock);

/**
 * Attravo can fire two /cart/change.js removals before the first one has
 * finished. Because Shopify's `line` identifier is positional, the first
 * removal can shift the second item's line number. The stale second request
 * then leaves one item behind even though Attravo optimistically showed an
 * empty cart.
 *
 * Serialize only cart mutation requests. When several line removals overlap,
 * compensate for earlier successful removals before sending the queued
 * request. Normal one-at-a-time cart interactions are unchanged.
 */
(function installBaseformCartMutationQueue() {
  if (window.__baseformCartMutationQueue) return;

  const mutationSuffixes = ['/cart/change', '/cart/update', '/cart/clear'];
  const upstreamFetch = window.fetch.bind(window);
  const originalXhrOpen = XMLHttpRequest.prototype.open;
  const originalXhrSend = XMLHttpRequest.prototype.send;

  let queueTail = Promise.resolve();
  let queuedCount = 0;
  /** @type {number[]} */
  let removedOriginalLines = [];

  function normalizedPath(input) {
    try {
      const rawUrl =
        typeof input === 'string' || input instanceof URL
          ? input.toString()
          : input instanceof Request
            ? input.url
            : '';

      if (!rawUrl) return '';
      return new URL(rawUrl, window.location.origin).pathname.replace(/\.js$/, '').replace(/\/+$/, '');
    } catch (_) {
      return '';
    }
  }

  function isCartMutation(input) {
    const path = normalizedPath(input);
    return mutationSuffixes.some((suffix) => path === suffix || path.endsWith(suffix));
  }

  function isCartChange(input) {
    const path = normalizedPath(input);
    return path === '/cart/change' || path.endsWith('/cart/change');
  }

  /**
   * @param {BodyInit | null | undefined} body
   * @returns {{ line: number | null, quantity: number | null, kind: string, data: any }}
   */
  function readMutationBody(body) {
    try {
      if (typeof body === 'string') {
        const data = JSON.parse(body);
        return {
          line: Number.isFinite(Number(data?.line)) ? Number(data.line) : null,
          quantity: Number.isFinite(Number(data?.quantity)) ? Number(data.quantity) : null,
          kind: 'json',
          data,
        };
      }

      if (body instanceof URLSearchParams) {
        const line = Number(body.get('line'));
        const quantity = Number(body.get('quantity'));
        return {
          line: Number.isFinite(line) && line > 0 ? line : null,
          quantity: Number.isFinite(quantity) ? quantity : null,
          kind: 'search',
          data: body,
        };
      }

      if (body instanceof FormData) {
        const line = Number(body.get('line'));
        const quantity = Number(body.get('quantity'));
        return {
          line: Number.isFinite(line) && line > 0 ? line : null,
          quantity: Number.isFinite(quantity) ? quantity : null,
          kind: 'form',
          data: body,
        };
      }
    } catch (_) {
      // Unknown bodies are still serialized; they simply can't be line-remapped.
    }

    return { line: null, quantity: null, kind: 'unknown', data: body };
  }

  function withAdjustedLine(bodyMeta, adjustedLine) {
    if (!bodyMeta || adjustedLine < 1) return bodyMeta?.data;

    if (bodyMeta.kind === 'json') {
      return JSON.stringify({ ...bodyMeta.data, line: adjustedLine });
    }

    if (bodyMeta.kind === 'search') {
      const next = new URLSearchParams(bodyMeta.data);
      next.set('line', String(adjustedLine));
      return next;
    }

    if (bodyMeta.kind === 'form') {
      const next = new FormData();
      for (const [key, value] of bodyMeta.data.entries()) next.append(key, value);
      next.set('line', String(adjustedLine));
      return next;
    }

    return bodyMeta.data;
  }

  function adjustedLineFor(originalLine) {
    if (!originalLine) return originalLine;

    const earlierSuccessfulRemovals = removedOriginalLines.reduce(
      (count, removedLine) => count + (removedLine < originalLine ? 1 : 0),
      0
    );

    return Math.max(1, originalLine - earlierSuccessfulRemovals);
  }

  function enqueueMutation(task) {
    if (queuedCount === 0) removedOriginalLines = [];
    queuedCount += 1;

    const result = queueTail.then(task, task);

    queueTail = result
      .catch(() => undefined)
      .finally(() => {
        queuedCount -= 1;
        if (queuedCount === 0) removedOriginalLines = [];
      });

    return result;
  }

  window.fetch = (...args) => {
    if (!isCartMutation(args[0])) return upstreamFetch(...args);

    const init = args[1] ?? {};
    const bodyMeta = readMutationBody(init.body);
    const originalLine = isCartChange(args[0]) ? bodyMeta.line : null;
    const isRemoval = originalLine !== null && bodyMeta.quantity === 0;

    return enqueueMutation(async () => {
      let requestArgs = args;

      if (originalLine !== null) {
        const adjustedLine = adjustedLineFor(originalLine);
        if (adjustedLine !== originalLine) {
          requestArgs = [
            args[0],
            {
              ...init,
              body: withAdjustedLine(bodyMeta, adjustedLine),
            },
          ];
        }
      }

      const response = await upstreamFetch(...requestArgs);
      let mutationSucceeded = response.ok;

      if (mutationSucceeded && isRemoval) {
        try {
          const payload = await response.clone().json();
          mutationSucceeded = !payload?.errors;
        } catch (_) {
          // A 2xx response is sufficient when the app returns a non-JSON body.
        }
      }

      if (mutationSucceeded && isRemoval) {
        removedOriginalLines.push(originalLine);
      }

      return response;
    });
  };

  const xhrUrl = Symbol('baseformCartMutationUrl');

  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    this[xhrUrl] = url;
    return originalXhrOpen.call(this, method, url, ...rest);
  };

  XMLHttpRequest.prototype.send = function (body) {
    if (!isCartMutation(this[xhrUrl])) {
      return originalXhrSend.call(this, body);
    }

    const xhr = this;
    const bodyMeta = readMutationBody(body);
    const originalLine = isCartChange(xhr[xhrUrl]) ? bodyMeta.line : null;
    const isRemoval = originalLine !== null && bodyMeta.quantity === 0;

    enqueueMutation(
      () =>
        new Promise((resolve, reject) => {
          let requestBody = body;

          if (originalLine !== null) {
            const adjustedLine = adjustedLineFor(originalLine);
            if (adjustedLine !== originalLine) {
              requestBody = withAdjustedLine(bodyMeta, adjustedLine);
            }
          }

          const settle = () => {
            xhr.removeEventListener('loadend', settle);
            xhr.removeEventListener('error', fail);

            if (isRemoval && xhr.status >= 200 && xhr.status < 300) {
              let mutationSucceeded = true;

              try {
                const payload =
                  xhr.responseType === 'json' ? xhr.response : JSON.parse(xhr.responseText || 'null');
                mutationSucceeded = !payload?.errors;
              } catch (_) {
                // Keep the HTTP success result for non-JSON responses.
              }

              if (mutationSucceeded) removedOriginalLines.push(originalLine);
            }

            resolve();
          };

          const fail = () => {
            xhr.removeEventListener('loadend', settle);
            xhr.removeEventListener('error', fail);
            reject(new Error('Cart mutation request failed'));
          };

          xhr.addEventListener('loadend', settle);
          xhr.addEventListener('error', fail);

          try {
            originalXhrSend.call(xhr, requestBody);
          } catch (error) {
            fail();
          }
        })
    );

    return undefined;
  };

  Object.defineProperty(window, '__baseformCartMutationQueue', {
    value: true,
    configurable: false,
  });
})();


/* Separate link + disclosure button for desktop mega menus.
   Maintains hover support; supports keyboard focus, Escape and click-to-toggle. */
(function initBaseformDesktopDisclosures() {
  function setup(root = document) {
    root.querySelectorAll('[data-bf-nav-parent]').forEach((parent) => {
      if (parent.dataset.bfNavReady === 'true') return;
      const button = parent.querySelector('[data-bf-nav-toggle]');
      const submenu = parent.querySelector('.bf-header__mega');
      if (!button || !submenu) return;
      parent.dataset.bfNavReady = 'true';

      function show() {
        parent.removeAttribute('data-bf-nav-dismissed');
        parent.classList.add('bf-nav-open');
        button.setAttribute('aria-expanded', 'true');
      }
      function hide(dismiss = false) {
        parent.classList.remove('bf-nav-open');
        button.setAttribute('aria-expanded', 'false');
        if (dismiss) parent.setAttribute('data-bf-nav-dismissed', '');
        else parent.removeAttribute('data-bf-nav-dismissed');
      }

      parent.addEventListener('mouseenter', show);
      parent.addEventListener('mouseleave', () => hide());
      parent.addEventListener('focusin', () => {
        if (!parent.hasAttribute('data-bf-nav-dismissed')) show();
      });
      parent.addEventListener('focusout', (event) => {
        if (!parent.contains(event.relatedTarget)) hide();
      });
      button.addEventListener('click', (event) => {
        event.preventDefault();
        if (button.getAttribute('aria-expanded') === 'true') hide(true);
        else show();
      });
      parent.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        event.stopPropagation();
        hide(true);
        button.focus();
      });
      document.addEventListener('pointerdown', (event) => {
        if (!parent.contains(event.target)) hide();
      });
    });
  }
  setup();
  document.addEventListener('shopify:section:load', (event) => {
    setup(event.target instanceof Element ? event.target : document);
  });
})();
